import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  ArrowLeft, MapPin, CalendarDays, Users, Clock, User, XCircle, CheckCircle,
} from "lucide-react";
import { toast } from "sonner";

export default function GameDetail() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: game, isLoading, error } = useQuery({
    queryKey: ["member-game", gameId],
    queryFn: () => api.get(`/games/${gameId}`),
  });

  const interestMut = useMutation({
    mutationFn: () => api.post(`/games/${gameId}/interest`),
    onSuccess: () => {
      toast.success("Interest expressed");
      queryClient.invalidateQueries({ queryKey: ["member-game", gameId] });
      queryClient.invalidateQueries({ queryKey: ["member-games"] });
    },
    onError: (e) => toast.error(e.message),
  });

  const closeMut = useMutation({
    mutationFn: (status) => api.patch(`/games/${gameId}`, { status }),
    onSuccess: () => {
      toast.success("Game updated");
      navigate("/games");
    },
    onError: (e) => toast.error(e.message),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-32" data-testid="game-detail-loading">
        <div className="w-12 h-0.5 bg-bronze mx-auto animate-pulse" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-32 px-6" data-testid="game-detail-error">
        <p className="text-cream text-sm">{error.message}</p>
      </div>
    );
  }

  const d = game.game_datetime ? new Date(game.game_datetime) : null;
  const isOpen = game.status === "OPEN";

  return (
    <div className="max-w-2xl mx-auto" data-testid="game-detail-page">
      <Link
        to="/games"
        className="inline-flex items-center gap-1.5 text-xs tracking-[0.15em] uppercase text-lv-muted hover:text-bronze transition-colors mb-8"
        data-testid="game-detail-back"
      >
        <ArrowLeft size={14} />
        Games
      </Link>

      {/* Status badge for non-open */}
      {!isOpen && (
        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-[0.25em] uppercase bg-walnut border border-lv-border text-lv-muted mb-4">
          {game.status}
        </div>
      )}

      <h1
        className="font-serif text-3xl sm:text-4xl text-cream tracking-wide mb-2"
        data-testid="game-detail-title"
      >
        {game.title}
      </h1>

      {/* Creator */}
      {game.creator && (
        <p className="text-xs text-lv-muted tracking-wide mb-6" data-testid="game-detail-creator">
          Posted by {game.creator.preferred_name} ({game.creator.member_code})
        </p>
      )}

      {/* Meta */}
      <div className="bg-walnut border border-lv-border p-5 sm:p-6 mb-6 space-y-3" data-testid="game-detail-meta">
        {d && (
          <div className="flex items-center gap-3">
            <CalendarDays size={15} className="text-bronze shrink-0" />
            <div>
              <p className="text-sm text-cream tracking-wide">
                {d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
              </p>
              <p className="text-xs text-sand/60 mt-0.5">
                {d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
              </p>
            </div>
          </div>
        )}
        {game.location && (
          <div className="flex items-center gap-3">
            <MapPin size={15} className="text-lv-muted/60 shrink-0" />
            <span className="text-sm text-sand/80 tracking-wide">{game.location}</span>
          </div>
        )}
        <div className="flex items-center gap-3">
          <Users size={15} className="text-lv-muted/60 shrink-0" />
          <span className="text-sm text-sand/80 tracking-wide">
            {game.min_players}–{game.max_players} players
          </span>
        </div>
        {game.level_preference && (
          <div className="flex items-center gap-3">
            <Clock size={15} className="text-lv-muted/60 shrink-0" />
            <span className="text-sm text-sand/80 tracking-wide">{game.level_preference}</span>
          </div>
        )}
      </div>

      {/* Description */}
      {game.description && (
        <div className="bg-walnut border border-lv-border p-5 sm:p-6 mb-6" data-testid="game-detail-description">
          <p className="text-sm text-sand/80 leading-relaxed whitespace-pre-line">
            {game.description}
          </p>
        </div>
      )}

      {/* Interested members (visible to all, names shown) */}
      {game.interested_members?.length > 0 && (
        <div className="bg-walnut border border-lv-border p-5 sm:p-6 mb-6" data-testid="game-detail-interested">
          <p className="text-xs tracking-[0.25em] uppercase text-lv-muted mb-4">
            Interested ({game.interest_count})
          </p>
          <div className="space-y-3">
            {game.interested_members.map((m, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <User size={14} className="text-bronze/60 shrink-0" />
                <span className="text-sm text-cream tracking-wide">{m.preferred_name}</span>
                <span className="text-xs text-lv-muted font-mono">{m.member_code}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      {isOpen && (
        <div className="space-y-3" data-testid="game-detail-actions">
          {!game.is_mine && !game.i_expressed_interest && (
            <button
              onClick={() => interestMut.mutate()}
              disabled={interestMut.isPending}
              className="lv-btn-primary w-full min-h-[48px]"
              data-testid="game-express-interest-btn"
            >
              {interestMut.isPending ? "Expressing Interest\u2026" : "I\u2019m Interested"}
            </button>
          )}
          {!game.is_mine && game.i_expressed_interest && (
            <div
              className="flex items-center justify-center gap-2 py-3 text-sm text-lv-active tracking-wide"
              data-testid="game-interest-confirmed"
            >
              <CheckCircle size={16} />
              You expressed interest
            </div>
          )}
          {game.is_mine && (
            <div className="flex gap-3">
              <button
                onClick={() => closeMut.mutate("CLOSED")}
                disabled={closeMut.isPending}
                className="lv-btn-primary flex-1 min-h-[48px]"
                data-testid="game-close-btn"
              >
                Close Game
              </button>
              <button
                onClick={() => closeMut.mutate("CANCELLED")}
                disabled={closeMut.isPending}
                className="lv-btn-secondary flex-1 min-h-[48px]"
                data-testid="game-cancel-btn"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
